import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import liffManager from '../../../lib/liff';
import apiClient from '../../../lib/api';
import { LinkedPatient } from '../../../hooks/api/useLinkedPatients';
import { getOnboardingData } from '../../../lib/onboardingSession';
import {
  LoadingSpinner,
  PageContainer,
  ContentCard,
  ContentCardTitle,
  ContentCardHeaderSection,
  PrimaryButton,
  TextButton,
} from '../../../components';

interface UserProfileProps {
  onComplete: () => void | Promise<void>;
  onProfileAlreadyComplete?: () => void | Promise<void>;
  onBack?: () => void;
}

const LineAccountCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  gap: ${({ theme }) => theme.spacing.xs};
  background: ${({ theme }) => theme.colors.pageBackground};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const LineAccountLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DisplayName = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  line-height: 150%;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Required = styled.span`
  color: ${({ theme }) => theme.colors.status.error};
  margin-left: 4px;
`;

const DateInputGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
`;

// 生年月日セレクトの幅比率。年は「YYYY年」で月日より文字数が多いため広めに取る。
// 年 : 月 : 日 = 120 : 83.5 : 83.5（≒ 1.44 : 1 : 1）
const YEAR_FLEX = 120;
const MONTH_DAY_FLEX = 83.5;

const DateSelect = styled.select<{ $flex: number }>`
  flex: ${({ $flex }) => $flex} 1 0;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing.ms};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostalCodeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const PostalCodeInput = styled.input`
  width: 100px;
  padding: ${({ theme }) => theme.spacing.ms};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  text-align: center;
  box-sizing: border-box;

  &:first-of-type {
    width: 80px;
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostalCodeSeparator = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.ms};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  font-family: inherit;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const RadioOption = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing.ms};
  height: 55px;
  cursor: pointer;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.backgroundTableGood : theme.colors.background};
  border: 1px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.primary : theme.colors.border.medium};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const RadioInput = styled.input`
  margin: 0;
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.colors.primary};
`;

const RadioLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  line-height: 130%;
  color: ${({ theme }) => theme.colors.text.primary};
  user-select: none;
`;

const HelperText = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ValidatingText = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-style: italic;
`;

const ErrorText = styled.p`
  margin: 4px 0 0;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.status.error};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
`;

const AddressDisplay = styled.div`
  padding: ${({ theme }) => theme.spacing.ms};
  background: ${({ theme }) => theme.colors.pageBackground};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const AddressField = styled.div`
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.xs};

  &:last-child {
    margin-bottom: 0;
  }
`;

const AddressLabel = styled.span`
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  min-width: 80px;
  margin-right: ${({ theme }) => theme.spacing.ms};
`;

const AddressValue = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

// 本人連携時に生年月日・性別を読み取り専用で表示するボックス
const ReadOnlyValue = styled.div`
  padding: ${({ theme }) => theme.spacing.ms};
  background: ${({ theme }) => theme.colors.pageBackground};
  border: 1px solid ${({ theme }) => theme.colors.border.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorBox = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTableDanger};
  color: ${({ theme }) => theme.colors.status.error};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  border-left: 4px solid ${({ theme }) => theme.colors.status.error};
`;

export const UserProfile: React.FC<UserProfileProps> = ({ onComplete, onProfileAlreadyComplete, onBack }) => {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState<string>('');
  // Birth date fields - default to 1970
  const [birthYear, setBirthYear] = useState<string>('1970');
  const [birthMonth, setBirthMonth] = useState<string>('');
  const [birthDay, setBirthDay] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  // Postal code fields
  const [postalCode1, setPostalCode1] = useState<string>('');
  const [postalCode2, setPostalCode2] = useState<string>('');
  const [occupation, setOccupation] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  // 初期値を true にして、AppReadyGate のローディング表示 → UserProfile マウント直後の
  // 1 フレーム＋ setTimeout(1000) の間にフォームの一部がフラッシュ表示されるのを防ぐ。
  // 下の useEffect で fetchExistingProfile 内 finally により false に戻る。
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [error, setError] = useState<string>('');
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSelfUser, setIsSelfUser] = useState<boolean>(false);
  // 本人連携時の読み取り専用表示用（patients テーブル由来の生年月日・性別）
  const [selfPatientBirthDate, setSelfPatientBirthDate] = useState<string | null>(null);
  const [selfPatientGender, setSelfPatientGender] = useState<string | null>(null);
  // Address fields
  const [prefecture, setPrefecture] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [town, setTown] = useState<string>('');
  const [isValidatingPostalCode, setIsValidatingPostalCode] = useState(false);
  const [postalCodeError, setPostalCodeError] = useState<string>('');

  // Fetch existing profile data
  const fetchExistingProfile = async () => {
    setIsLoadingProfile(true);
    try {
      // Check if LIFF is initialized
      if (!liffManager.isLoggedIn()) {
        // Get display name from LINE profile only
        const profile = await liffManager.getProfile();
        if (profile?.displayName) {
          setDisplayName(profile.displayName);
        }
        setIsLoadingProfile(false);
        return;
      }

      const lineUserId = await liffManager.getLineUserId();
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '';

      if (!lineUserId) {
        setIsLoadingProfile(false);
        return;
      }

      const response = await fetch(`${apiUrl}/api/user/profile?line_user_id=${lineUserId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('lineIdToken') || ''}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // setDisplayName(data.display_name || '');
        const profile = await liffManager.getProfile();
        if (profile?.displayName) {
          setDisplayName(profile.displayName);
        }

        // Parse birth date
        if (data.birth_date) {
          const [year, month, day] = data.birth_date.split('-');
          setBirthYear(year || '1970');
          setBirthMonth(month ? parseInt(month).toString() : '');
          setBirthDay(day ? parseInt(day).toString() : '');
        }

        setGender(data.gender || '');

        // Parse postal code
        if (data.postal_code) {
          const [code1, code2] = data.postal_code.split('-');
          setPostalCode1(code1 || '');
          setPostalCode2(code2 || '');
        }

        setOccupation(data.occupation || '');

        // If profile exists, set to edit mode
        if (data.profile_completed) {
          // オンボーディング中かつプロフィール登録済みの場合、即座に家族連携を実行
          const onboardingData = getOnboardingData();
          if (onboardingData && onProfileAlreadyComplete) {
            try {
              await onProfileAlreadyComplete();
              return;
            } catch (linkErr) {
              console.error('家族連携に失敗しました:', linkErr);
              setError(linkErr instanceof Error ? linkErr.message : '家族連携に失敗しました。再度お試しください。');
              // フォールバック: 編集モードで表示し、保存ボタン経由で再試行可能にする
              // onboardingDataは confirmAndLinkFamily 内で回復不能エラー時のみクリアされる
            }
          }
          setIsEditMode(true);
        }
      } else if (response.status === 404) {
        // User profile doesn't exist yet, get display name from LINE
        const profile = await liffManager.getProfile();
        if (profile?.displayName) {
          setDisplayName(profile.displayName);
        }
      }

      // Check if user is 本人 (patient themselves)
      // 「本人連携が1つでもある」なら本人扱い。
      // 本人連携を持つユーザーは施設に利用者登録済みで、生年月日・性別は patients テーブルが
      // 真実の情報源となるため、UserProfile 画面では patients の値を読み取り専用で表示する。
      // オンボーディング中はsessionStorageから判定（家族連携前のためAPIには未登録）
      const onboardingData = getOnboardingData();
      const hasSelfLink = (patients: LinkedPatient[] | undefined): boolean =>
        !!patients && patients.some((p) => p.relationship === '本人');
      // 本人連携の患者から birth_date/gender を抜き出して、読み取り専用表示用に保持する
      const applySelfPatientReadOnly = (patients: LinkedPatient[] | undefined) => {
        const selfLink = patients?.find((p) => p.relationship === '本人');
        setSelfPatientBirthDate(selfLink?.birth_date ?? null);
        setSelfPatientGender(selfLink?.gender ?? null);
      };

      // 既存連携はキャッシュ優先で参照し、無ければ API フォールバック取得する。
      // /menu/profile は linkedPatients を事前取得しないためキャッシュ未作成のことがあり、
      // その場合でも（オンボーディング中か既存ユーザーかに関わらず）本人連携の有無を
      // 正しく判定できるよう、判定の前にキャッシュ → API の順で取得しておく。
      let patients: LinkedPatient[] | undefined =
        queryClient.getQueryData<LinkedPatient[]>(['linkedPatients']);
      if (!patients) {
        try {
          const linkedData = await apiClient.getLinkedPatients();
          patients = linkedData.patients;
        } catch (linkErr) {
          console.warn('Failed to check relationship:', linkErr);
        }
      }

      if (onboardingData) {
        // 新規に張ろうとしている連携 + 既存連携 のいずれかに本人連携があれば本人扱い
        const isAddingSelfLink = onboardingData.relationship === '本人';
        setIsSelfUser(isAddingSelfLink || hasSelfLink(patients));
        // 新規に本人連携を張る最中（isAddingSelfLink）は、その患者がまだ linkedPatients に
        // 含まれないため生年月日・性別は「未登録」表示になる。ただし招待リンクの本人選択時は
        // UserProfile 画面を経由せず家族連携が完了する（LinkToken 側で処理）ため、この経路に
        // isAddingSelfLink=true で到達するのはフォールバック時のレアケースで実害はない。
        applySelfPatientReadOnly(patients);
      } else {
        // 既存ユーザー（編集モード）
        setIsSelfUser(hasSelfLink(patients));
        applySelfPatientReadOnly(patients);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      // Fallback to LINE profile
      try {
        const profile = await liffManager.getProfile();
        if (profile?.displayName) {
          setDisplayName(profile.displayName);
        }
      } catch (lineErr) {
        console.error('Failed to fetch LINE profile:', lineErr);
        setDisplayName('ユーザー様');
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    // Wait a bit for LIFF to initialize before fetching profile
    const timer = setTimeout(() => {
      fetchExistingProfile();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Validate postal code and fetch address
  const validatePostalCode = async (code1: string, code2: string) => {
    if (code1.length !== 3 || code2.length !== 4) {
      return;
    }

    setIsValidatingPostalCode(true);
    setPostalCodeError('');

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '';
      const postalCode = `${code1}${code2}`;

      const response = await fetch(`${apiUrl}/api/postal-code/validate?code=${postalCode}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.valid) {
        // Set address fields automatically
        setPrefecture(data.prefecture || '');
        setCity(data.city || '');
        setTown(data.town || '');
        setPostalCodeError('');
      } else {
        setPostalCodeError('該当する郵便番号が見つかりません');
        // Clear address fields
        setPrefecture('');
        setCity('');
        setTown('');
      }
    } catch (err) {
      console.error('Failed to validate postal code:', err);
      setPostalCodeError('郵便番号の検証中にエラーが発生しました');
    } finally {
      setIsValidatingPostalCode(false);
    }
  };

  const handlePostalCode1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value.length <= 3) {
      setPostalCode1(value);
      // Auto-focus next field when 3 digits are entered
      if (value.length === 3) {
        const nextField = document.getElementById('postalCode2');
        if (nextField) nextField.focus();
      }
    }
  };

  const handlePostalCode2Change = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    if (value.length <= 4) {
      setPostalCode2(value);
      // Validate when complete
      if (value.length === 4 && postalCode1.length === 3) {
        await validatePostalCode(postalCode1, value);
      }
    }
  };

  // Generate year options (130 years from current year to allow up to 130 years old)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 131 }, (_, i) => currentYear - i); // From current year to 130 years ago

  // Generate month options (1-12)
  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1);

  // Generate day options (1-31)
  const getDayOptions = () => {
    if (!birthMonth || !birthYear) return Array.from({ length: 31 }, (_, i) => i + 1);

    const month = parseInt(birthMonth);
    const year = parseInt(birthYear);
    const daysInMonth = new Date(year, month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  const validateForm = (): boolean => {
    // 本人以外: 生年月日と性別が必須
    if (!isSelfUser) {
      if (!birthYear || !birthMonth || !birthDay) {
        setError('生年月日を入力してください');
        return false;
      }

      if (!gender) {
        setError('性別を選択してください');
        return false;
      }
    }

    // 編集モード時のみ: 郵便番号と職業をチェック
    if (isEditMode) {
      if (postalCode1.length !== 3 || postalCode2.length !== 4) {
        setError('郵便番号を正しく入力してください（例：123-4567）');
        return false;
      }

      if (!occupation) {
        setError('職業を選択してください');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL || '';

      // Format birth date as YYYY-MM-DD
      const birthDate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
      // Format postal code as XXX-XXXX
      const postalCode = `${postalCode1}-${postalCode2}`;

      // Get ID token - try localStorage first, then LIFF as fallback
      let idToken = localStorage.getItem('lineIdToken');

      if (!idToken) {
        try {
          // Ensure LIFF is initialized
          if (!liffManager.isLoggedIn()) {
            await liffManager.init();
          }

          idToken = await liffManager.getIdToken();

          if (idToken) {
            // Save to localStorage for future use
            localStorage.setItem('lineIdToken', idToken);
          }
        } catch (liffError) {
          console.error('Failed to get ID token from LIFF:', liffError);
          throw new Error('認証情報の取得に失敗しました。アプリを再起動してください。');
        }
      }

      if (!idToken) {
        throw new Error('認証情報が見つかりません。再度ログインしてください。');
      }

      // Prepare request body based on mode
      const requestBody: any = {
        display_name: displayName,
      };

      // 本人以外は生年月日・性別を含める
      if (!isSelfUser) {
        requestBody.birth_date = birthDate;
        requestBody.gender = gender;
      }

      // 編集モード時のみ郵便番号と職業を含める
      if (isEditMode) {
        requestBody.postal_code = postalCode;
        requestBody.occupation = occupation;
      }

      const response = await fetch(`${apiUrl}/api/user/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Status code based error messages
        let errorMessage = 'プロフィールの保存に失敗しました';
        if (response.status === 401) {
          errorMessage = '認証エラー: ログインし直してください';
        } else if (response.status === 403) {
          errorMessage = '権限エラー: この操作を行う権限がありません';
        } else if (response.status === 400) {
          errorMessage = `入力エラー: ${errorData.error || '入力内容を確認してください'}`;
        } else if (response.status >= 500) {
          errorMessage = 'サーバーエラー: しばらく待ってから再度お試しください';
        }

        throw new Error(errorData.error || errorMessage);
      }

      // Mark profile as complete in localStorage
      localStorage.setItem('userProfileCompleted', 'true');

      // Call completion callback
      await onComplete();
      // 成功時は onComplete() 内の goHome() で画面遷移するため、
      // ここで setIsLoading(false) を呼ぶと遷移直前にプロフィールフォームが一瞬再描画される。
      // isLoading は true のまま維持し、遷移完了に任せる。
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'エラーが発生しました';
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  const occupationOptions = [
    '会社員',
    '公務員',
    '自営業',
    '医療・福祉',
    '教育関係',
    '主婦・主夫',
    '学生',
    '無職',
    'その他',
  ];

  const titleText = isEditMode
    ? 'プロフィール情報を更新してください。'
    : 'プロフィールを登録してください。';

  // 本人連携時の読み取り専用表示用ヘルパー
  const formatReadOnlyBirthDate = (d: string | null): string => {
    if (!d) return '未登録';
    const [y, m, day] = d.split('-');
    if (!y || !m || !day) return '未登録';
    return `${y}年${parseInt(m, 10)}月${parseInt(day, 10)}日`;
  };
  const formatReadOnlyGender = (g: string | null): string => {
    switch (g) {
      case 'male':
        return '男性';
      case 'female':
        return '女性';
      case 'other':
        return 'その他';
      default:
        return '未登録';
    }
  };

  // 「登録する／更新する」ボタンの活性条件
  const isFormFilled = (() => {
    if (!isSelfUser) {
      if (!birthYear || !birthMonth || !birthDay || !gender) return false;
    }
    if (isEditMode) {
      if (postalCode1.length !== 3 || postalCode2.length !== 4) return false;
      if (!occupation) return false;
    }
    return true;
  })();

  if (isLoading) {
    return <LoadingSpinner fullPage message="保存中..." />;
  }

  // AppReadyGate(authOnly) の「読み込み中...」→ プロフィール情報読込中 → 保存中 を
  // 同一の LoadingSpinner DOM で連続させ、ローディング画面が点滅しないようにする。
  if (isLoadingProfile) {
    return <LoadingSpinner fullPage message="プロフィール情報を読み込み中..." />;
  }

  return (
    <PageContainer>
      <ContentCard>
        <ContentCardHeaderSection>
          <ContentCardTitle>{titleText}</ContentCardTitle>
        </ContentCardHeaderSection>

        <LineAccountCard>
          <LineAccountLabel>LINEアカウント名</LineAccountLabel>
          <DisplayName>{displayName}</DisplayName>
        </LineAccountCard>

        {/* 生年月日: 本人連携時は patients の値を読み取り専用、それ以外は入力 */}
        <FormGroup>
          <Label>
            生年月日
            {!isSelfUser && <Required>*</Required>}
          </Label>
          {isSelfUser ? (
            <ReadOnlyValue>{formatReadOnlyBirthDate(selfPatientBirthDate)}</ReadOnlyValue>
          ) : (
            <DateInputGroup>
              <DateSelect
                $flex={YEAR_FLEX}
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value)}
              >
                <option value="">年</option>
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}年
                  </option>
                ))}
              </DateSelect>

              <DateSelect
                $flex={MONTH_DAY_FLEX}
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
              >
                <option value="">月</option>
                {monthOptions.map((month) => (
                  <option key={month} value={month}>
                    {month}月
                  </option>
                ))}
              </DateSelect>

              <DateSelect
                $flex={MONTH_DAY_FLEX}
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
              >
                <option value="">日</option>
                {getDayOptions().map((day) => (
                  <option key={day} value={day}>
                    {day}日
                  </option>
                ))}
              </DateSelect>
            </DateInputGroup>
          )}
        </FormGroup>

        {/* 性別: 本人連携時は patients の値を読み取り専用、それ以外は選択 */}
        <FormGroup>
          <Label>
            性別
            {!isSelfUser && <Required>*</Required>}
          </Label>
          {isSelfUser ? (
            <ReadOnlyValue>{formatReadOnlyGender(selfPatientGender)}</ReadOnlyValue>
          ) : (
            <RadioGroup>
              {[
                { value: 'male', label: '男性' },
                { value: 'female', label: '女性' },
                { value: 'other', label: 'その他' },
              ].map((opt) => (
                <RadioOption key={opt.value} $selected={gender === opt.value}>
                  <RadioInput
                    type="radio"
                    name="gender"
                    value={opt.value}
                    checked={gender === opt.value}
                    onChange={(e) => setGender(e.target.value)}
                  />
                  <RadioLabel>{opt.label}</RadioLabel>
                </RadioOption>
              ))}
            </RadioGroup>
          )}
        </FormGroup>

        {isEditMode && (
          <FormGroup>
            <Label>
              郵便番号 <Required>*</Required>
            </Label>
            <PostalCodeGroup>
              <PostalCodeInput
                type="tel"
                id="postalCode1"
                value={postalCode1}
                onChange={handlePostalCode1Change}
                placeholder="123"
                maxLength={3}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <PostalCodeSeparator>-</PostalCodeSeparator>
              <PostalCodeInput
                type="tel"
                id="postalCode2"
                value={postalCode2}
                onChange={handlePostalCode2Change}
                placeholder="4567"
                maxLength={4}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </PostalCodeGroup>
            <HelperText>
              半角数字で入力してください
              {isValidatingPostalCode && (
                <ValidatingText> (検証中...)</ValidatingText>
              )}
            </HelperText>
            {postalCodeError && <ErrorText>{postalCodeError}</ErrorText>}
          </FormGroup>
        )}

        {isEditMode && (prefecture || city || town) && (
          <FormGroup>
            <Label>住所（自動入力）</Label>
            <AddressDisplay>
              <AddressField>
                <AddressLabel>都道府県:</AddressLabel>
                <AddressValue>{prefecture}</AddressValue>
              </AddressField>
              <AddressField>
                <AddressLabel>市区町村:</AddressLabel>
                <AddressValue>{city}</AddressValue>
              </AddressField>
              {town && (
                <AddressField>
                  <AddressLabel>町域:</AddressLabel>
                  <AddressValue>{town}</AddressValue>
                </AddressField>
              )}
            </AddressDisplay>
          </FormGroup>
        )}

        {isEditMode && (
          <FormGroup>
            <Label>
              職業 <Required>*</Required>
            </Label>
            <Select
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
            >
              <option value="">選択してください</option>
              {occupationOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </FormGroup>
        )}

        {error && <ErrorBox>{error}</ErrorBox>}

        <PrimaryButton onClick={handleSubmit} disabled={isLoading || !isFormFilled}>
          {isEditMode ? '更新する' : '登録する'}
        </PrimaryButton>

        {onBack && (
          <TextButton onClick={onBack} aria-label="戻る">
            戻る
          </TextButton>
        )}
      </ContentCard>
    </PageContainer>
  );
};

export default UserProfile;
